/**
 * spendingApi.js — Phase 86.C.
 *
 * Client for the ONE spending breakdown service. Both the Dashboard and an
 * account's Analysis tab call this with a different `accountIds` scope and
 * nothing else different, which is what stops the two views from disagreeing.
 */
import axios from 'axios';
import { supabase } from '@/lib/supabaseClient';

const API = process.env.REACT_APP_BACKEND_URL;

export const SPENDING_PERIODS = [
  { value: 'this_month',    label: 'This month' },
  { value: 'last_month',    label: 'Last month' },
  { value: 'last_30_days',  label: 'Last 30 days' },
  { value: 'last_3_months', label: 'Last 3 months' },
  { value: 'custom',        label: 'Custom' },
];

async function authHeaders() {
  let { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    await new Promise((r) => setTimeout(r, 700));
    ({ data: { session } } = await supabase.auth.getSession());
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * @param {object}   opts
 * @param {string[]} [opts.accountIds] omit for every eligible account
 * @param {string}   [opts.period]     this_month | last_month | last_30_days | last_3_months | custom
 * @param {string}   [opts.start]      YYYY-MM-DD, required when period=custom
 * @param {string}   [opts.end]        YYYY-MM-DD, required when period=custom
 * @param {string}   [opts.grouping]   category | merchant
 */
export async function fetchSpendingBreakdown({
  accountIds, period = 'this_month', start, end, grouping = 'category', compare = true,
} = {}) {
  const params = new URLSearchParams({ period, grouping, compare: String(compare) });
  if (accountIds?.length) params.set('account_ids', accountIds.join(','));
  if (period === 'custom') {
    if (start) params.set('start', start);
    if (end) params.set('end', end);
  }
  // One retry. The shared Supabase connection can be closed while idle, which
  // surfaces as a 500 (`RemoteProtocolError: Server disconnected`) on an
  // otherwise healthy request — not something the user should have to see.
  for (const attempt of [1, 2]) {
    try {
      const res = await axios.get(`${API}/api/spending/breakdown?${params.toString()}`, {
        headers: await authHeaders(), timeout: 25000,
      });
      return res.data;
    } catch (e) {
      if (attempt === 2) {
        console.warn('spending breakdown failed:', e?.response?.status || e?.message);
        return null;
      }
      await new Promise((r) => setTimeout(r, 900));
    }
  }
  return null;
}
